import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { PrepChatMessage } from '../types/charterWsTypes'
import type { StructuredProfile } from '../types/charterTypes'

const STREAMING_ID_PREFIX = 'stream_'

export interface PrepMonitorSession {
  messages: PrepChatMessage[]
  completedSections: Record<string, string>
  activeSectionKey: string | null
  profile: StructuredProfile | null
  sharingMode: string
  visibleSections: string[]
  sectionOrder: string[]
  profileApproved: boolean
  isConnected: boolean
  isStreaming: boolean
  error: string | null
}

interface PrepMonitorState {
  sessions: Record<string, PrepMonitorSession>
}

const defaultSession = (): PrepMonitorSession => ({
  messages: [],
  completedSections: {},
  activeSectionKey: null,
  profile: null,
  sharingMode: 'auto',
  visibleSections: [],
  sectionOrder: [],
  profileApproved: false,
  isConnected: false,
  isStreaming: false,
  error: null,
})

const initialState: PrepMonitorState = { sessions: {} }

const charterPrepMonitorSlice = createSlice({
  name: 'charterPrepMonitor',
  initialState,
  reducers: {
    initMemberSession(state, action: PayloadAction<string>) {
      if (!state.sessions[action.payload]) {
        state.sessions[action.payload] = defaultSession()
      }
    },

    resetMemberSession(state, action: PayloadAction<string>) {
      state.sessions[action.payload] = defaultSession()
    },

    removeMemberSession(state, action: PayloadAction<string>) {
      delete state.sessions[action.payload]
    },

    setMonitorConnected(
      state,
      action: PayloadAction<{ memberId: string; connected: boolean }>
    ) {
      const { memberId, connected } = action.payload
      if (!state.sessions[memberId]) state.sessions[memberId] = defaultSession()
      state.sessions[memberId].isConnected = connected
      if (!connected) state.sessions[memberId].isStreaming = false
    },

    setMonitorHistory(
      state,
      action: PayloadAction<{
        memberId: string
        messages: PrepChatMessage[]
        completed_sections: Record<string, string>
        active_section_key: string | null
        profile: Record<string, unknown> | null
        sharing_mode: string
        visible_sections: string[]
        section_order: string[]
        profile_approved: boolean
      }>
    ) {
      const { memberId, ...data } = action.payload
      if (!state.sessions[memberId]) state.sessions[memberId] = defaultSession()
      const s = state.sessions[memberId]
      s.messages = data.messages
      s.completedSections = data.completed_sections
      s.activeSectionKey = data.active_section_key
      s.profile = data.profile as StructuredProfile | null
      s.sharingMode = data.sharing_mode
      s.visibleSections = data.visible_sections
      s.sectionOrder = data.section_order
      s.profileApproved = data.profile_approved
    },

    appendMonitorChunk(
      state,
      action: PayloadAction<{ memberId: string; stream_id: string; content: string }>
    ) {
      const { memberId, stream_id, content } = action.payload
      if (!state.sessions[memberId]) state.sessions[memberId] = defaultSession()
      const s = state.sessions[memberId]
      const tempId = STREAMING_ID_PREFIX + stream_id
      const existing = s.messages.find((m) => m.id === tempId)
      if (existing) {
        existing.content += content
      } else {
        s.messages.push({
          id: tempId,
          role: 'assistant',
          content,
          is_streaming: true,
          created_at: new Date().toISOString(),
        })
        s.isStreaming = true
      }
    },

    finalizeMonitorMessage(
      state,
      action: PayloadAction<{
        memberId: string
        stream_id: string
        content: string
        section_key?: string
      }>
    ) {
      const { memberId, stream_id, content, section_key } = action.payload
      if (!state.sessions[memberId]) return
      const s = state.sessions[memberId]
      const tempId = STREAMING_ID_PREFIX + stream_id
      const idx = s.messages.findIndex((m) => m.id === tempId)
      if (idx !== -1) {
        s.messages[idx] = {
          ...s.messages[idx],
          id: `final_${stream_id}`,
          content,
          section_key,
          is_streaming: false,
        }
      }
      s.isStreaming = false
    },

    markMonitorSectionComplete(
      state,
      action: PayloadAction<{
        memberId: string
        section_key: string
        completed_sections: Record<string, string>
        active_section_key: string | null
      }>
    ) {
      const { memberId, completed_sections, active_section_key } = action.payload
      if (!state.sessions[memberId]) return
      state.sessions[memberId].completedSections = completed_sections
      state.sessions[memberId].activeSectionKey = active_section_key
    },

    setMonitorProfile(
      state,
      action: PayloadAction<{ memberId: string; profile: Record<string, unknown> }>
    ) {
      if (!state.sessions[action.payload.memberId]) return
      state.sessions[action.payload.memberId].profile =
        action.payload.profile as unknown as StructuredProfile
    },

    markMonitorProfileApproved(state, action: PayloadAction<string>) {
      if (state.sessions[action.payload]) {
        state.sessions[action.payload].profileApproved = true
      }
    },

    setMonitorError(
      state,
      action: PayloadAction<{ memberId: string; error: string | null }>
    ) {
      if (!state.sessions[action.payload.memberId]) return
      state.sessions[action.payload.memberId].error = action.payload.error
    },
  },
})

export const {
  initMemberSession,
  resetMemberSession,
  removeMemberSession,
  setMonitorConnected,
  setMonitorHistory,
  appendMonitorChunk,
  finalizeMonitorMessage,
  markMonitorSectionComplete,
  markMonitorProfileApproved,
  setMonitorProfile,
  setMonitorError,
} = charterPrepMonitorSlice.actions

export default charterPrepMonitorSlice.reducer
